/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 7 hook(s) need dependency review; fix each and remove this directive */
import {
  createContainer,
  createMergeEstimate,
  createMergeFile,
  emptyCurrentFolderInFileManager,
  minimiseFileUpload,
  processFileList,
  selectedFilesFileManager,
  setCurrentFolderInFileManager,
  refreshFileManager as refreshFileManagerFn,
} from "@Actions/fileUpload";
import { setCurrentPath } from "@Reducers/filesStatuses";
import axiosWrapper from "@AxiosWrapper";
import {
  DRAWING_TYPE,
  PDF_ENGINE,
  PDF_TYPE,
  SOMETHING_WENT_WRONG,
  TOAST_TYPE_ERROR,
  TOAST_TYPE_SUCCESS,
  TOAST_TYPE_WARNING,
} from "@Services/constants";
import { getBackendUrl } from "@Utils";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation, untranslated } from "@i18n";
import { useDispatch, useSelector } from "react-redux";
import { setSpaceToggle } from "@Reducers/AccessPermissionReducer";
import _ from "lodash";
import type {
  GetFileVersionDataResponse,
  ImportExternalVolumeFilesBody,
  StandardResponse,
} from "@Types/fileOperations";
import { showToastNotification } from "@Reducers/toastSlice";
import { getViewerUrl, MAX_FILE_UPLOAD_SIZE } from "@Helpers";
import { setViewerState } from "@Reducers/Viewer";
import {
  getContractDetailUrl,
  getEstimateUrl,
  getScheduleUrl,
  isFileNomenclatureCorrect,
} from "@Services/utils/helpers";
import { downloadFile } from "@Services/fileDownload";
import { formCategories, isMergeAllowed } from "@Helpers/constants";
import type { GetFormTemplateResponse } from "@ServerTypes/projects/project.schema";
import type { CreateFolderResponse } from "@ServerTypes/fileSystemServer/fileSystemServer.schema";
import type { ImportExternalVolumeFilesResponse } from "@ServerTypes/fileOperations/fileOperations.schemas";
import { fileFormatNotSupportedToast, UnnamedEstimateNotSupportedToast } from "@Actions/GlobalSearchAction";
import {
  setIsFileVersionUploading,
  setCurrentFileUploadProperties,
  setFileUploadProperties,
  maximiseFileUpload,
  closeFileUpload,
  setFileUploadData,
  emptyFileUploadData,
} from "@Reducers/minimiseFileUpload";
import useFileManagerComponent from "@CubeComponents/FileManager/useFileManagerComponent";
import useFileManagerShared from "@CubeComponents/FileManager/useFileManagerShared";
import useFormValidator from "../../../../features/forms/components/FormValidation/useFormValidator";
import { getCurrSpaceData } from "@Actions/breadcrumbsdata";
import { setSpaceData } from "@Reducers/breadcrumbsdata";

export const TAB_THIS_SPACE = "this";
export const TAB_EXTERNAL_SPACE = "external";
export const TAB_REVIEWS = "reviews";

interface NavigateLike {
  (location: string, options?: any): void;
}

interface LocationLike {
  pathname: string;
  search: string;
}

interface UseFileManagerProps {
  defaultSelectedTab?: string;
  fileManagerAsPopup?: boolean;
  spaceId: string;
  projectId: string;
  showOnlyMergedFile?: boolean;
  setShowProperties?: any;
  mobileFileManager?: boolean;
  hideExternalSpace?: boolean;
  hideReviews?: boolean;
  hideAllTabs?: boolean;
  enableSpaceListDropdown?: boolean;
  openThisSpaceByDefault?: boolean;
  createShortcut?: boolean;
  showFolderTree?: boolean;
  handleFileSelectionCallback?: (files: any[]) => void;
  hideIssues?: boolean;
  hideTasks?: boolean;
  hideEstimates?: boolean;
  hideClashTests?: boolean;
  hideContracts?: boolean;
  hideSchedules?: boolean;
  useAttachedExternalVolume?: boolean;
  hideMergeFiles?: boolean;
  disableContextMenu?: boolean;
  disableFiles?: any[];
  enableOnlyShareableResources?: boolean;
  hideUpload?: boolean;
  enableFilesTypes?: string[] | null;
  hideShortcuts?: boolean;
  handleFolderSelectionCallback?: (folder: any) => void;
  setSpaceId?: (id: any) => void;
  alreadySelected?: any[] | null;
  showExcelImport?: boolean;
  defaultPath?: string;
  allowFolderSelectionCallback?: boolean;
  spaceList?: any[];
  disableDragAndDropUpload?: boolean;
  navigate: NavigateLike;
  location: LocationLike;
  nativeMobile?: boolean;
}

type AxiosResponseLike<T> = { data: T };

function useFileManager({
  defaultSelectedTab = "this",
  fileManagerAsPopup = false,
  spaceId,
  projectId,
  showOnlyMergedFile = false,
  setShowProperties,
  mobileFileManager = false,
  hideExternalSpace = false,
  hideReviews = false,
  hideAllTabs = false,
  enableSpaceListDropdown = false,
  openThisSpaceByDefault = false,
  createShortcut = false,
  showFolderTree = true,
  handleFileSelectionCallback = () => {},
  hideIssues = false,
  hideTasks = false,
  hideEstimates = false,
  hideClashTests = false,
  hideContracts = false,
  hideSchedules = false,
  useAttachedExternalVolume = false,
  hideMergeFiles = false,
  disableContextMenu = false,
  disableFiles = [],
  enableOnlyShareableResources = false,
  hideUpload = false,
  enableFilesTypes = null,
  hideShortcuts = false,
  handleFolderSelectionCallback = () => {},
  allowFolderSelectionCallback = false,
  setSpaceId = () => {},
  alreadySelected = null,
  showExcelImport = true,
  defaultPath,
  spaceList,
  disableDragAndDropUpload = false,
  navigate,
  location,
  nativeMobile = false,
}: UseFileManagerProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch<any>();

  const fileManagerRef = useRef<any>(null);
  const formTemplateRef = useRef<any>({ current: {} });
  const formValidator: any = useFormValidator({});
  const fileUploadListRef = useRef<any>({ current: [] });
  const nomenclatureRef = useRef<any[]>([]);
  const spaceListRef = useRef<any>(null);
  const tabRef = useRef<any>({});
  const pollingRef = useRef<any>(null);

  // State
  const [fileManagerData, setFileManagerData] = useState<any>({
    cwd: {},
    files: [],
  });
  const [showContainerImportModal, setShowContainerImportModal] = useState<boolean>(false);
  const [importMode, setImportMode] = useState<"container" | "file" | null>(null);
  const [openRenameModal, setOpenRenameModal] = useState<any>({ state: false, oldName: "" });
  const [selectedFile, setSelectedFile] = useState<any>({});
  const [newName, setNewName] = useState<string>("");
  const [folderName, setFolderName] = useState<string>("");
  const [createReviewModal, setCreateReviewModal] = useState<{ show: boolean }>({ show: false });
  const [fileAllowedExtensions, setFileAllowedExtensions] = useState<any>({});
  const [openMergeModal, setOpenMergeModal] = useState<boolean>(false);
  const [openMergeEstimateModal, setOpenMergeEstimateModal] = useState<boolean>(false);
  const [emptyThisSpace, setEmptyThisSpace] = useState<boolean>(false);
  const [emptyExternal, setEmptyExternal] = useState<boolean>(false);
  const [openFolderCreationModal, setOpenFolderCreationModal] = useState<boolean>(false);
  const [openFileImport, setOpenFileImport] = useState<boolean>(false);
  const [openContainerModal, setOpenContainerModal] = useState<boolean>(false);
  const [currentFolder, setCurrentFolder] = useState<any>({ id: "0" });
  const [selectedFilesToDelete, setSelectedFilesToDelete] = useState<any>(null);
  const [openDeleteConfirmationModal, setOpenDeleteConfirmationModal] = useState<boolean>(false);
  const [openCreateTransmittalModal, setOpenCreateTransmittalModal] = useState<boolean>(false);
  const [filesToCopy, setFilesToCopy] = useState<any>(null);
  const [filesToCut, setFilesToCut] = useState<any>(null);
  const [formTemplateReceived, setFormTemplateReceived] = useState<any>({});
  const [containerFormTemplate, setContainerFormTemplate] = useState<any[]>([]);
  const [fileTypeValue, setFileTypeValue] = useState<string | null>(null);
  const [resourceType, setResourceType] = useState<any>({});
  const [containerName, setContainerName] = useState<string>("");
  const [nomenclatureValues, setNomenclatureValues] = useState<string[]>([]);
  const [draggedFiles, setDraggedFiles] = useState<any>([]);
  const [path, setPath] = useState<string | null>(null);
  const [preSearchText, setPreSearchText] = useState<string | null>("");
  const [toolBarBtnState, setToolBarBtnState] = useState<{
    upload: boolean;
    refresh: boolean;
    rename: boolean;
    delete: boolean;
    download: boolean;
    details: boolean;
    createNewShortcut: boolean;
    consume: boolean;
  }>({
    upload: !hideUpload,
    refresh: true,
    rename: false,
    delete: false,
    download: false,
    details: true,
    createNewShortcut: false,
    consume: false,
  });
  const [selectedTab, setSelectedTab] = useState<string>(defaultSelectedTab || TAB_THIS_SPACE);
  const [shortCutData, setShortCutData] = useState<any>({});
  const [openFileModalExternal, setOpenFileModalExternal] = useState<false | "consume" | "shortcut">(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [importSubmenuAnchor, setImportSubmenuAnchor] = useState<null | HTMLElement>(null);

  // Redux state — selected individually so missing slices (e.g. on mobile) don't break the hook
  const layout = useSelector((state: any) => state.layout) ?? {};
  const getSpaceToggle = useSelector((state: any) => state.getSpaceToggle);
  const projectData = useSelector((state: any) => state.projectData);
  const userDetails = useSelector((state: any) => state.userDetails);

  const fileUploadList = useSelector((state: any) => state.fileUploadList) ?? [];
  const isFileVersionUploading = useSelector((state: any) => state.isFileVersionUploading);
  const fileUploadState = useSelector((state: any) => state.fileUploadState);
  const spaceData = useSelector((state: any) => state.spaceData);
  const refreshFileManager = useSelector((state: any) => state.refreshFileManager);

  const searchParams = new URLSearchParams(location.search);
  const spaceType = searchParams.get("spaceType");
  const fileSearchText = searchParams.get("searchText");

  const open = Boolean(anchorEl);
  const importSubmenuOpen = Boolean(importSubmenuAnchor);

  // Handler functions
  const showToast = () => {
    dispatch(
      showToastNotification({
        content: t("Insufficient permission to perform this action."),
        type: TOAST_TYPE_ERROR,
      }),
    );
  };

  const handleDeleteClick = (selectedFiles: any[]) => {
    let isUserAllowedToDeleteAllFiles = true;
    for (const eachObj of selectedFiles) {
      if (!eachObj?.permission?.delete) {
        isUserAllowedToDeleteAllFiles = false;
      }
    }
    if (isUserAllowedToDeleteAllFiles) {
      setSelectedFilesToDelete(selectedFiles);
      setOpenDeleteConfirmationModal(true);
    } else {
      showToast();
    }
  };

  useEffect(() => {
    if (spaceId) {
      dispatch(getCurrSpaceData({ id: spaceId }));
    }
  }, [spaceId]);

  const handleDeleteModalClose = () => {
    setOpenDeleteConfirmationModal(false);
    setSelectedFilesToDelete(null);
  };

  const handleDeleteSuccess = (status: boolean) => {
    if (status) {
      handleDeleteModalClose();
      const deletedIds = selectedFilesToDelete.map((file: any) => file.id);
      if (deletedIds.length === fileManagerData.files?.length) {
        setEmptyExternal(selectedTab == TAB_EXTERNAL_SPACE);
        setEmptyThisSpace(selectedTab == TAB_THIS_SPACE);
      }
      setFileManagerData((prev: any) => ({
        ...prev,
        files: prev.files?.filter((file: any) => !deletedIds.includes(file.id)),
        filteredFiles: prev.filteredFiles
          ? prev.filteredFiles.filter((file: any) => !deletedIds.includes(file.id))
          : null,
      }));
      handleUnSelectAllOption();
      setToolBarBtnState(() => ({
        upload: !hideUpload,
        refresh: true,
        rename: false,
        delete: false,
        download: false,
        details: true,
        createNewShortcut: false,
        consume: false,
      }));
      setSelectedFilesToDelete(null);
    }
  };

  const getSelectedFilesInFileManager = () => {
    return fileManagerRef.current?.getSelectedFilesInFileManager() || [];
  };

  const handleRenameFile = (selectedFiles: any[]) => {
    const file = selectedFiles[0] ?? getSelectedFilesInFileManager()[0];
    if (!file?.permission?.write) {
      showToast();
    } else {
      setNewName(file.name);
      setOpenRenameModal({ state: true, oldName: file.name });
      setSelectedFile(file);
    }
  };

  const handleFileTransmit = (selectedFiles: any[]) => {
    const allFilesHaveSharePermission = selectedFiles.every((file: any) => file.permission.share);
    if (allFilesHaveSharePermission) {
      setSelectedFile(selectedFiles);
      setOpenCreateTransmittalModal(true);
    } else {
      showToast();
    }
  };

  const fetchConstituentFilesOfMergedFile = async (fileVersion: any) => {
    const mergedFile = (await axiosWrapper.post<GetFileVersionDataResponse>(
      `${getBackendUrl()}/fileOperations/getFileVersionData`,
      {
        fileVersionId: fileVersion,
      },
    )) as AxiosResponseLike<GetFileVersionDataResponse>;
    const constituentFiles = mergedFile.data.data?.sourceFileVersionId ?? [];
    if (constituentFiles.length === 0) {
      dispatch(
        showToastNotification({
          content: t("This merged file does not contain any files. Kindly add files to view."),
          type: TOAST_TYPE_WARNING,
        }),
      );
      return false;
    }
    return true;
  };

  const handleLocationOpen = (location: string, args?: any) => {
    if (!fileManagerAsPopup) {
      if (_.get(args, "resourceType") && _.get(args, "resourceType") != "RESOURCE_TYPE_FOLDER" && mobileFileManager) {
        (window as any)?.ReactNativeWebView?.postMessage(JSON.stringify({ action: "disableFileOpen", location }));
      } else {
        navigate(location);
      }
    }
  };

  const handleOpenOrPreloadAndOpen = async (selectedFiles: any[], id: string) => {
    if (id === "open" && ["kanban"].includes(selectedFiles[0].resourceType)) {
      handleLocationOpen("/kanban");
    }
    const fileId = resolveFileId(selectedFiles[0]);
    if (tabRef.current.isThisSpace && selectedFiles[0].resourceType === "RESOURCE_TYPE_FILE" && !fileId) {
      dispatch(
        showToastNotification({
          content: t("No file associated to open"),
          type: TOAST_TYPE_WARNING,
        }),
      );
      return;
    }
    if (
      ["RESOURCE_TYPE_MERGED_FILE", "RESOURCE_TYPE_FILE", "RESOURCE_TYPE_ISSUE", "RESOURCE_TYPE_EMBED"].includes(
        selectedFiles[0].resourceType,
      )
    ) {
      if (selectedFiles[0].resourceType === "RESOURCE_TYPE_MERGED_FILE") {
        const result = await fetchConstituentFilesOfMergedFile(selectedFiles[0].fileVersion);
        if (!result) {
          return;
        }
      }
      if (selectedFiles[0].resourceType === "RESOURCE_TYPE_ISSUE") {
        const locationUrl: any = getViewerUrl({
          fileVersionId: selectedFiles[0].fileId,
          issueId: selectedFiles[0].snapshot.issueId,
          projectId,
        });
        handleLocationOpen(locationUrl);
        return;
      }
      const viewerMode =
        id === "preloadAndOpen" ? "scs" : id === "loadWithoutProperties" ? "skipProperties" : undefined;
      const targetUrl: any = buildViewerUrl(selectedFiles[0], fileId, viewerMode);
      if (canOpenViewer(selectedFiles[0])) {
        handleLocationOpen(targetUrl);
      } else {
        showToast();
      }
    }
  };

  const handleDownloadFile = (selectedFiles: any[]) => {
    if (searchParams.get("spaceType") === TAB_THIS_SPACE) {
      const files = selectedFiles;
      const fileVersionIds: any[] = [];
      const folderIds: any[] = [];
      let isDownloadAllowed = true;
      files.forEach((item: any) => {
        const fileExtension = item.name.split(".").pop();
        item.isFile ? fileVersionIds.push(item.fileVersion) : folderIds.push(item._id);
        isDownloadAllowed = item.permission?.download && isDownloadAllowed;
        if (fileExtension === PDF_TYPE[0]) {
          return;
        }
      });
      if (isDownloadAllowed) {
        downloadFile({
          fileVersionIds,
          folderIds,
          type: "Download",
          mobileFileManager,
          fileName: _.get(files, "[0].name"),
        });
      } else {
        showToast();
      }
    } else {
      showToast();
    }
  };

  // Export a DWG/DXF drawing to PDF via the cad2pdf microservice and download the result.
  const handleExportToPdf = (selectedFiles: any[]) => {
    const file = selectedFiles?.[0];
    if (!file) return;
    const ext = (file.name?.split(".").pop() || "").toLowerCase();
    if (!["dwg", "dxf"].includes(ext)) {
      dispatch(
        showToastNotification({
          content: t("Only DWG/DXF files can be exported to PDF."),
          type: TOAST_TYPE_WARNING,
        }),
      );
      return;
    }
    if (file.permission && file.permission.download === false) {
      showToast();
      return;
    }
    const url = new URL(`${getBackendUrl()}/cad2pdf/download`);
    url.searchParams.set("fileVersionId", file.fileVersion);
    url.searchParams.set("fileName", file.name);
    dispatch(showToastNotification({ content: t("Preparing PDF…"), type: TOAST_TYPE_SUCCESS }));
    axiosWrapper
      .get<Blob>(url.toString(), { responseType: "blob" })
      .then((response: any) => {
        const blob = response.data;
        // Backend returns application/pdf on success; anything else means the export failed.
        if (blob && blob.type && blob.type.indexOf("pdf") === -1) {
          throw new Error("PDF export failed");
        }
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `${file.name.replace(/\.[^.]+$/, "")}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
      })
      .catch((error: any) => {
        console.error("[cad2pdf] export failed:", error);
        dispatch(
          showToastNotification({
            content: t("PDF export failed"),
            type: TOAST_TYPE_ERROR,
          }),
        );
      });
  };

  const getPermission = () => {
    const value = currentFolder || {};
    if (Object.hasOwn(value, "permission")) {
      return value.permission.upload;
    }
    return false;
  };

  const isFileItem = (name: string) => name?.includes(".");

  const processDroppedFiles = (sortFilesList: any[]): boolean => {
    if (!sortFilesList.length) return false;
    if (!getPermission()) {
      showToast();
      return false;
    }

    const firstIsFile = isFileItem(sortFilesList[0]?.name);
    if (!firstIsFile) {
      dispatch(showToastNotification({ content: t("Folder cannot be dragged"), type: TOAST_TYPE_ERROR }));
      return false;
    }

    const notContainFolder = sortFilesList.some((f: any) => isFileItem(f?.name));
    if (!notContainFolder) return false;

    setDraggedFiles(sortFilesList);

    if (fileUploadState === "MINIMISED") {
      dispatch(
        showToastNotification({
          content: t("Please close other upload operations before proceeding!"),
          type: TOAST_TYPE_ERROR,
        }),
      );
      return false;
    }

    dispatch(maximiseFileUpload());
    dispatch(
      setCurrentFileUploadProperties({
        formTemplate: formTemplateRef.current,
        currentFolderRef: currentFolder,
        isUploadAllowed: getPermission(),
        fileNomenclature: _.get(currentFolder, "fileNomenclature"),
        workspaceId: spaceId,
        hierarchyId: currentFolder.hierarchyId,
      }),
    );

    const currentFiles = fileManagerData?.files?.filter((f: any) => f.parentId == currentFolder.id) || [];
    const processed = processFileList({
      fileList: [...sortFilesList],
      currentFiles,
      fileNomenclature: _.get(currentFolder, "fileNomenclature"),
      isFileVersionUploading,
      uploadFolderData: {},
      uploadSpaceId: spaceId,
      selectedSpaceTypeInFileManager: searchParams.get("spaceType") ?? undefined,
      currentFolder,
    });

    dispatch(setFileUploadData(processed));
    return true;
  };

  const handleEmptyStateDrop = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (disableDragAndDropUpload || selectedTab != TAB_THIS_SPACE) return;
    if (!getPermission()) {
      showToast();
      return;
    }
    const files = Array.from(event.dataTransfer?.files || []);
    processDroppedFiles(files);
  };

  const handleEmptyStateDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const beforeSending = (args: any) => {
    const setVisibilityFlags = () => {
      if (hideIssues) args.hideIssues = true;
      if (hideTasks) args.hideTasks = true;
      if (hideEstimates) args.hideEstimates = true;
      if (hideClashTests) args.hideClashTests = true;
      if (hideSchedules) args.hideSchedules = true;
      if (hideContracts) args.hideContracts = true;
      if (hideMergeFiles) args.hideMergeFiles = true;
      if (hideShortcuts) args.hideShortcuts = true;
      if (useAttachedExternalVolume) args.useAttachedExternalVolume = true;
      args.showOnlyMergedFile = !!showOnlyMergedFile;
    };

    const ensureDataArray = () => {
      if (Array.isArray(args.data) && args.data.length) {
        args.data = args.data.map((d: any) => ({ ...d, spaceId, spaceType: selectedTab }));
        return;
      }
      args.data = [{ spaceId, spaceType: selectedTab }];
    };

    const handleUpload = () => {
      const sortFilesList: any[] = Object.values(args.files || {});
      processDroppedFiles(sortFilesList);
      args.cancel = true;
    };

    const handleCopy = () => {
      args.targetData.spaceId ||= spaceId;

      const invalid = !Array.isArray(args.data) || args.data.some((obj: any) => !Object.hasOwn(obj, "fileVersion"));

      if (invalid) {
        args.cancel = true;
        dispatch(showToastNotification({ content: t("Container cannot be copy pasted"), type: TOAST_TYPE_ERROR }));
      }
    };

    switch (args.action) {
      case "upload":
        handleUpload();
        return;
      case "copy":
        handleCopy();
        break;
      case "read":
        setDraggedFiles(null);
        break;
      default:
        break;
    }

    ensureDataArray();
    setVisibilityFlags();
  };

  const handleResourceCut = (selectedFiles: any[]) => {
    setFilesToCut(selectedFiles);
    setFilesToCopy(null);
  };

  const handleResourceCopy = (selectedFiles: any[]) => {
    let userAllowedToCopyAllFiles = true;
    selectedFiles?.map((file: any) => {
      if (!file.permission?.copy) {
        userAllowedToCopyAllFiles = false;
      }
    });
    if (userAllowedToCopyAllFiles) {
      setFilesToCopy(selectedFiles);
      setFilesToCut(null);
    } else {
      showToast();
    }
  };

  const handleResourcePaste = async (selectedFolder: any = null) => {
    if (selectedFolder && selectedFolder.length > 1) {
      dispatch(
        showToastNotification({
          content: t("Select one folder to paste content to"),
          type: TOAST_TYPE_WARNING,
        }),
      );
      return;
    }
    if (!filesToCopy && !filesToCut) {
      dispatch(
        showToastNotification({
          content: t("No resource selected to paste"),
          type: TOAST_TYPE_WARNING,
        }),
      );
      return;
    }
    const destinationFolder = selectedFolder[0] ?? currentFolder;
    const isuserAllowedToPaste = destinationFolder?.permission?.write;
    if (!isuserAllowedToPaste) {
      showToast();
      return;
    }
    let actionFiles: any;
    if (filesToCopy) {
      actionFiles = filesToCopy;
    } else if (filesToCut) {
      actionFiles = filesToCut;
    }
    if (actionFiles) {
      const allParams: any = {
        action: filesToCopy ? "copy" : "move",
        data: actionFiles,
        targetData: destinationFolder,
        renameFiles: [],
      };
      beforeSending(allParams);
      if (!allParams.cancel) {
        fileManagerRef.current?.showLoading();
        const fsUrl = `${getBackendUrl()}/fileSystemServer/${allParams.action === "copy" ? "copyFiles" : "moveFiles"}`;
        const res = await axiosWrapper.post<CreateFolderResponse>(fsUrl, {
          ...allParams,
        });

        if (!res.data.status) {
          dispatch(
            showToastNotification({
              content: untranslated(res.data?.errorMessage || res.data?.error?.code || "") || SOMETHING_WENT_WRONG,
              type: TOAST_TYPE_ERROR,
            }),
          );
          fileManagerRef.current?.hideLoading();
        } else {
          refreshCurrentFolder();
        }
        setFilesToCopy(null);
        setFilesToCut(null);
      }
    }
  };

  const handleSelectAllOption = () => {
    const getCondition = (file: any) => {
      if (!currentFolder.resourceType || currentFolder.resourceType == "RESOURCE_TYPE_TRANSMITTAL") {
        return file.transmittalVersionId == currentFolder.id;
      } else if (currentFolder.resourceType == "RESOURCE_TYPE_FOLDER") {
        return file.parentId == currentFolder.id;
      }
      return false;
    };
    const currentFiles = fileManagerData.files?.filter((file: any) => getCondition(file));
    const allRowIndexes = [...Array(currentFiles.length).keys()];
    fileManagerRef.current?.setSelectedRows(allRowIndexes);
  };

  const handleUnSelectAllOption = () => {
    fileManagerRef.current?.setSelectedRows([]);
  };

  const reloadFileManager = () => {
    if (fileManagerRef.current) {
      fileManagerRef.current?.refreshFileManager();
    }
  };

  const refreshCurrentFolder = () => {
    if (fileManagerRef.current) {
      fileManagerRef.current?.refreshFiles();
    }
  };

  const handleFileManagerTabClick = (type: string) => {
    if ([TAB_THIS_SPACE, TAB_EXTERNAL_SPACE].includes(type)) {
      dispatch(setCurrentPath("/"));
    }
    if ([TAB_EXTERNAL_SPACE, TAB_REVIEWS].includes(type) && !fileManagerAsPopup) {
      setShowProperties?.(false);
    }
    if (selectedTab == type) {
      return false;
    }

    setSelectedTab(type);

    const searchParams = new URLSearchParams(location.search);
    let url = "";
    searchParams.set("spaceType", type);
    if (!fileManagerAsPopup) {
      url = `${location.pathname}?${searchParams.toString()}`;
    }
    handleLocationOpen(url);
  };

  const handleSpaceSelect = (args: any) => {
    if (args.isInteracted) {
      setSpaceId(args.value);
    }
  };

  const handleSpaceToggle = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (spaceId) {
      await dispatch(
        setSpaceToggle({
          spaceId,
          toggle: event.target.checked,
        }),
      );
      if (fileManagerRef.current) {
        refreshCurrentFolder();
      }
    }
  };

  const handleDetailIconClick = () => {
    const selectedFile = getSelectedFilesInFileManager();

    if (mobileFileManager) {
      (window as any)?.ReactNativeWebView?.postMessage(
        JSON.stringify({
          action: "properties",
          data: selectedFile[0],
        }),
      );
    } else if (setShowProperties) {
      setShowProperties((prev: any) => !prev);
    }
  };

  const getFormTemplate = async () => {
    if (projectId && currentFolder.hierarchyId) {
      const response = await axiosWrapper.post<GetFormTemplateResponse>(`${getBackendUrl()}/projects/getFormTemplate`, {
        projectId,
        hierarchyId: currentFolder.hierarchyId,
        resourceType: formCategories.files,
      });

      if (response.data.status && response.data.data) {
        const template = response.data?.data?.template || [];
        setContainerFormTemplate(template);
        formTemplateRef.current = {
          template,
          formTemplateId: response.data.data._id,
        };
      }
      setFormTemplateReceived((prev) => ({
        ...(prev as Record<string, boolean>),
        [currentFolder.hierarchyId]: true,
      }));
    }
  };

  const handleFileAndFolderUpload = (args: any) => {
    setFormTemplateReceived(false);
    if (fileUploadState == "CLOSED") {
      dispatch(emptyFileUploadData());
    }
    if (
      !fileUploadListRef.current.length ||
      (fileUploadListRef.current[0]?.hierarchyId == currentFolder.hierarchyId &&
        fileUploadListRef.current[0]?.spaceId == spaceId)
    ) {
      if (!fileUploadListRef.current.length) {
        getFormTemplate();
      }
      dispatch(
        setFileUploadProperties({
          [`${spaceId}_${currentFolder.hierarchyId}`]: {
            currentFiles: fileManagerData?.files?.filter((file: any) => file.parentId == currentFolder.id) || [],
            workspaceId: spaceId,
            fileNomenclature: _.get(currentFolder, "fileNomenclature", ""),
            isUploadAllowed: getPermission(),
            formTemplate: formTemplateRef.current,
          },
        }),
      );
      dispatch(
        setCurrentFileUploadProperties({
          currentFiles: fileManagerData?.files?.filter((file: any) => file.parentId == currentFolder.id) || [],
          workspaceId: spaceId,
          hierarchyId: currentFolder.hierarchyId,
          selectedSpaceTypeInFileManager: selectedTab,
          fileAllowedExtensions,
          draggedFiles,
          isFolderUpload: args.item.id === "folderUpload",
          fileNomenclature: _.get(currentFolder, "fileNomenclature", ""),
          isUploadAllowed: getPermission(),
          formTemplate: formTemplateRef.current,
          formTemplateReceived,
          fileUploadStateRef: { current: "MAXIMISED" },
          currentFolderRef: currentFolder,
        }),
      );
      dispatch(maximiseFileUpload());
    } else {
      dispatch(
        showToastNotification({
          content: t("Another upload is in progress. Please close that popup to proceed."),
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
  };

  const handleSelectOption = (args: any) => {
    if (["fileUpload", "folderUpload"].includes(args.item.id)) {
      if (args.item.id === "folderUpload" && !getPermission()) {
        showToast();
        return;
      }
      handleFileAndFolderUpload(args);
    } else if (args.item.id == "merge") {
      setOpenMergeModal(true);
    } else if (args.item.id == "newFolder") {
      setOpenFolderCreationModal(true);
    } else if (args.item.id == "container") {
      refreshCurrentFolder();
      setOpenContainerModal(true);
    } else if (args.item.id == "fileImport") {
      setOpenFileImport(true);
    } else if (args.item.id == "importContainer") {
      setImportMode("container");
      setShowContainerImportModal(true);
    } else if (args.item.id == "importFile") {
      setImportMode("file");
      setShowContainerImportModal(true);
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    const permissionCheck = getPermission();
    if (permissionCheck) {
      setAnchorEl(event.currentTarget);
    } else {
      showToast();
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    setImportSubmenuAnchor(null);
  };

  const handleImportSubmenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setImportSubmenuAnchor(event.currentTarget);
  };

  const handleImportSubmenuClose = () => {
    setImportSubmenuAnchor(null);
  };

  const closeReviewModal = () => {
    setCreateReviewModal({ show: false });
  };

  const onFileSelect = (args: any[]) => {
    const fileList = args;
    let fileCount = 0;
    let estimateCount = 0;
    let scheduleCount = 0;
    let contractCount = 0;
    const toolBtnState = {
      rename: fileList.length == 1,
      delete: fileList.length > 0,
      download: fileList.length > 0,
      consume: fileList.length > 0 && fileList[0].resourceType != "RESOURCE_TYPE_TRANSMITTAL",
      createNewShortcut: fileList.length > 0 && fileList[0].resourceType != "RESOURCE_TYPE_TRANSMITTAL",
    };
    fileList.forEach((file: any) => {
      const resourceType = file.resourceType;

      if (resourceType == "RESOURCE_TYPE_ESTIMATE") {
        estimateCount = 1;
      } else if (resourceType == "RESOURCE_TYPE_SCHEDULE") {
        scheduleCount = 1;
      } else if (resourceType == "RESOURCE_TYPE_CONTRACT") {
        contractCount = 1;
      } else {
        if (file.isEmptyContainer || resourceType == "RESOURCE_TYPE_EMBED") {
          toolBtnState.download = false;
        }
        fileCount = 1;
      }
    });
    if (!fileManagerAsPopup) {
      if (fileList.length == 0) {
        dispatch(selectedFilesFileManager([]));
      } else {
        dispatch(selectedFilesFileManager(args));
      }
    }

    if (fileCount + estimateCount + scheduleCount > 1) {
      toolBtnState.delete = false;
    }
    if (estimateCount || scheduleCount || contractCount) {
      toolBtnState.download = false;
    }

    setToolBarBtnState((prev) => ({
      ...prev,
      ...toolBtnState,
    }));

    if (typeof handleFileSelectionCallback === "function") {
      handleFileSelectionCallback(args);
    }
  };

  const isShortcutType = (resourceTypeStr: string) =>
    resourceTypeStr === "RESOURCE_TYPE_SHORTCUT_FILE" || resourceTypeStr === "RESOURCE_TYPE_SHORTCUT_MERGED_FILE";

  const handleDirectNav = (row: any) => {
    switch (row.resourceType) {
      case "RESOURCE_TYPE_ESTIMATE": {
        handleLocationOpen(getEstimateUrl({ projectId, estimateId: row.estimateId }), row);
        return true;
      }
      case "RESOURCE_TYPE_SCHEDULE": {
        handleLocationOpen(getScheduleUrl({ projectId, scheduleId: row.schId }), row);
        return true;
      }
      case "RESOURCE_TYPE_CONTRACT": {
        handleLocationOpen(
          getContractDetailUrl({ projectId, contractId: row.contractId, selectedTab: "details" }),
          row,
        );
        return true;
      }
      default:
        return false;
    }
  };

  const ensureFileEngineOk = (row: any) => {
    if (row.resourceType !== "RESOURCE_TYPE_FILE") return true;
    if (!row.engineType) {
      dispatch(
        showToastNotification({
          content: t("Select an engine to view the file"),
          type: TOAST_TYPE_WARNING,
        }),
      );
      return false;
    }
    if (row.engineType === PDF_ENGINE) {
      dispatch(setViewerState({ isSidebarOpen: false }));
    }
    return true;
  };

  const resolveFileId = (row: any) => {
    if (row.resourceType === "RESOURCE_TYPE_ISSUE") {
      return row.fileVersion || row.fileId;
    }
    if (isShortcutType(row.resourceType)) {
      return row.sourceFileVersionId;
    }
    return row.fileVersion;
  };

  const buildViewerUrl = (row: any, fileId: any, mode?: "scs" | "skipProperties") => {
    const base: any = { fileVersionId: fileId, spaceId, projectId, shortcutParentId: row.parentId };
    if (tabRef.current.isExternalSpace) base.transmittalVersionId = row.transmittalVersionId;
    if (row.resourceType === "RESOURCE_TYPE_ISSUE") base.issueId = row.issueId;
    if (mode) base.mode = mode;
    return getViewerUrl(base);
  };

  const canOpenViewer = (row: any) =>
    tabRef.current.isExternalSpace || row?.permission?.read || row.resourceType !== "RESOURCE_TYPE_FILE";

  const onFileClick = async (clickedRow: any) => {
    if (!clickedRow?.isFile || fileManagerAsPopup) return;

    if (handleDirectNav(clickedRow)) return;

    if (clickedRow.resourceType === "RESOURCE_TYPE_MERGED_FILE") {
      const ok = await fetchConstituentFilesOfMergedFile(clickedRow.fileVersion);
      if (!ok) return;
    }

    if (!ensureFileEngineOk(clickedRow)) return;

    const fileId = resolveFileId(clickedRow);
    if (tabRef.current.isThisSpace && clickedRow.resourceType === "RESOURCE_TYPE_FILE" && !fileId) {
      dispatch(
        showToastNotification({
          content: t("No file associated to open"),
          type: TOAST_TYPE_WARNING,
        }),
      );
      return;
    }

    const targetUrl: any = buildViewerUrl(clickedRow, fileId);

    if (canOpenViewer(clickedRow)) {
      handleLocationOpen(targetUrl, clickedRow);
    } else {
      showToast();
    }
  };

  const toolbarClick = (args: any, id: string) => {
    const selectedFiles = getSelectedFilesInFileManager();

    if (id == "refresh") {
      refreshCurrentFolder();
    } else if (id == "delete") {
      handleDeleteClick(selectedFiles);
    } else if (id == "rename") {
      handleRenameFile(selectedFiles);
    } else if (id == "download") {
      handleDownloadFile(selectedFiles);
    } else if (id == "consume") {
      setOpenFileModalExternal("consume");
      setShortCutData(selectedFiles);
    } else if (id == "createNewShortcut") {
      if (selectedFiles[0].folderType == 0) {
        setShortCutData({
          transmittalId: selectedFiles[0].transmittalVersionId,
          fileVersionIds: selectedFiles.map((x: any) => x.fileVersion),
        });
        setOpenFileModalExternal("shortcut");
      }
    }
  };

  const onSuccess = (args: any) => {
    if (createShortcut || allowFolderSelectionCallback) {
      if (args?.result?.cwd?.name == "This Space" && allowFolderSelectionCallback) {
        handleFolderSelectionCallback({ spaceId });
      } else if (args?.result?.cwd?.name != "This Space") {
        handleFolderSelectionCallback({
          ...args.result.cwd,
          spaceId,
        });
      }
    }

    if (args.result.files?.length == 0) {
      if (selectedTab == TAB_THIS_SPACE && args.result.cwd?.hierarchyId == "0" && args.action != "search") {
        setEmptyThisSpace(true);
      } else if (selectedTab == TAB_EXTERNAL_SPACE && !args.result.cwd?.transmittalVersionId) {
        setEmptyExternal(true);
      }
    } else if (args.result.cwd?.hierarchyId == "0") {
      setEmptyThisSpace(false);
    } else {
      setEmptyExternal(false);
    }
  };

  const saveFileMergeModal = async (listOfSelectedFiles: any, mergeFileName: string) => {
    const sourceFileVersionId = Object.values(listOfSelectedFiles).map((val: any) => {
      if (val.isEmptyContainer) {
        return val.id;
      }
      return val.fileVersion;
    });
    const fileObjArray = Object.values(listOfSelectedFiles);
    const fileSettings: any = isMergeAllowed(fileObjArray);

    const mergeParams: any = {
      sourceFileVersionId,
      originalName: mergeFileName,
      parentId: currentFolder.hierarchyId,
      spaceId,
      folderType: currentFolder.folderType ?? 0,
    };
    if (!fileSettings.mergedAllowed) {
      dispatch(fileFormatNotSupportedToast());
    } else if (fileSettings.isFile) {
      if (!currentFolder?.permission?.upload) {
        showToast();
        return;
      }
      const response: any = await createMergeFile(mergeParams);
      if (response.data.status) {
        if (!fileManagerAsPopup) {
          dispatch(selectedFilesFileManager([]));
        }
        if (typeof handleFileSelectionCallback === "function") {
          handleFileSelectionCallback([]);
        }
        closeFileMergeModal();
        refreshCurrentFolder();
        dispatch(
          showToastNotification({
            content: t("Files merged successfully."),
            type: TOAST_TYPE_SUCCESS,
          }),
        );
      } else {
        dispatch(
          showToastNotification({
            content: response.data.errorMessage,
            type: TOAST_TYPE_WARNING,
          }),
        );
      }
    } else if (!fileSettings.isFile) {
      await createMergeEstimateModal(listOfSelectedFiles, mergeFileName);
    }
  };

  const closeFileMergeModal = () => {
    setOpenMergeModal(false);
  };

  const closeCreateShortcutModal = () => {
    setOpenFileModalExternal(false);
  };

  const closeFileImportPopup = () => {
    setOpenFileImport(false);
  };

  const closeMergeEstimateModal = () => {
    setOpenMergeEstimateModal(false);
  };

  const handleFileImportCallback = async (listOfSelectedFiles: any) => {
    closeFileImportPopup();
    if (Object.keys(listOfSelectedFiles).length) {
      const payload: ImportExternalVolumeFilesBody = {
        files: Object.keys(listOfSelectedFiles),
        spaceId,
        parentId: currentFolder._id,
      };
      const response = (await axiosWrapper.post<ImportExternalVolumeFilesResponse>(
        `${getBackendUrl()}/fileOperations/importExternalVolumeFiles`,
        payload,
      )) as AxiosResponseLike<StandardResponse<unknown>>;
      return response;
    }
  };

  const createMergeEstimateModal = async (listOfSelectedFiles: any, mergeEstimateName: string) => {
    const constituentEstimateIdArray = Object.values(listOfSelectedFiles).map((val: any) => val.estimateId);

    const params: any = {
      mergeEstimateName,
      spaceId,
      projectId,
      constituentEstimateIds: constituentEstimateIdArray,
    };

    const response: any = await createMergeEstimate(params);
    if (!response.data.status) {
      dispatch(UnnamedEstimateNotSupportedToast(response.data.errorMessage));
    } else if (response.data.status) {
      closeFileMergeModal();
      dispatch(
        showToastNotification({
          content: t("Estimates have been merged successfully."),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
    }
    setOpenMergeEstimateModal(false);
    refreshCurrentFolder();
  };

  const onFileLoad = (files: any[]) => {
    files.forEach((file: any) => {
      if (file?.permission?.read === false) {
        file.isRowDisabled = true;
      }
      if (fileManagerAsPopup && enableOnlyShareableResources && file?.permission?.share === false) {
        file.isRowDisabled = true;
      }
      if (
        Array.isArray(disableFiles) &&
        ["RESOURCE_TYPE_FILE", "RESOURCE_TYPE_MERGED_FILE"].includes(file.resourceType) &&
        disableFiles.includes(file.isEmptyContainer ? file.id : file.fileVersion)
      ) {
        file.isRowDisabled = true;
      }
      if (
        Array.isArray(disableFiles) &&
        file.resourceType == "RESOURCE_TYPE_ISSUE" &&
        disableFiles.includes(file?.issueVersionId)
      ) {
        file.isRowDisabled = true;
      }
      if (
        Array.isArray(disableFiles) &&
        file.resourceType == "RESOURCE_TYPE_TASKS" &&
        disableFiles.includes(file?.taskId)
      ) {
        file.isRowDisabled = true;
      }
      if (
        Array.isArray(enableFilesTypes) &&
        !enableFilesTypes.includes(file?.resourceType) &&
        !enableFilesTypes.includes(file?.engineType) &&
        !["RESOURCE_TYPE_FOLDER", "RESOURCE_TYPE_TRANSMITTAL"].includes(file?.resourceType)
      ) {
        file.isRowDisabled = true;
      }
    });
  };

  const handleNoDataRefresh = () => {
    reloadFileManager();
  };

  const handleModalClose = () => {
    setOpenContainerModal(false);
    setContainerName("");
    setFileTypeValue("");
    setResourceType({});
    nomenclatureRef.current = [];
    setNomenclatureValues([]);
  };

  const handleCreateContainer = async () => {
    const nameOfContainer = containerName;
    const typeOfContainer = fileTypeValue;
    const fileNomenclatureCorrect = isFileNomenclatureCorrect(nameOfContainer, currentFolder.fileNomenclature);
    if (!fileNomenclatureCorrect) {
      dispatch(
        showToastNotification({
          content: t("File nomenclature is incorrect"),
          type: TOAST_TYPE_ERROR,
        }),
      );
    } else if (
      resourceType.value == "RESOURCE_TYPE_EMBED" ||
      (typeof formValidator.validateAll == "function" && formValidator.validateAll())
    ) {
      handleModalClose();
      const customFields: any = {};
      containerFormTemplate?.forEach((section: any) => {
        section.questionFields.forEach((item: any) => {
          if (item.answer?.value) {
            customFields[item._id] = item.answer.value;
          }
        });
      });
      const res: any = await createContainer({
        name: nameOfContainer,
        type: typeOfContainer,
        hierarchyId: currentFolder.hierarchyId,
        folderType: currentFolder.folderType,
        spaceId,
        customFields,
        resourceType: resourceType.value,
      });
      const response: any = {};
      if (res.data.status) {
        refreshCurrentFolder();
        response.content = `Container ${nameOfContainer} has been created successfully!`;
      } else {
        response.content = SOMETHING_WENT_WRONG;
        response.type = TOAST_TYPE_ERROR;
      }
      dispatch(showToastNotification(response));
    }
  };

  const handleSaveNewName = async () => {
    const oldName = openRenameModal.oldName;
    setOpenRenameModal({ state: false, oldName: "" });

    const allParams: any = {
      action: "rename",
      path: "/",
      name: oldName,
      newName,
      showFileExtension: true,
      data: [selectedFile],
    };
    beforeSending(allParams);
    if (!allParams.cancel) {
      fileManagerRef.current?.showLoading();
      const res = await axiosWrapper.post<CreateFolderResponse>(`${getBackendUrl()}/fileSystemServer/renameFile`, {
        ...allParams,
      });
      if (!res.data.status) {
        dispatch(
          showToastNotification({
            content: untranslated(res.data?.errorMessage ?? ""),
            type: TOAST_TYPE_ERROR,
          }),
        );
      } else {
        const fileNomenclature = _.get(currentFolder, "fileNomenclature");
        const isNomenclatureCorrect = isFileNomenclatureCorrect(newName, fileNomenclature);
        const newFiles = structuredClone(fileManagerData?.files)?.map((f: any) => {
          if (f.id == selectedFile.id) {
            f.name = newName;
            f.isFileNomenclatureCorrect = isNomenclatureCorrect;
            f.dateModified = Date.now();
            f.modifiedBy = userDetails.fullName;
          }
          return f;
        });
        const newObj: any = {
          files: newFiles,
        };
        if (fileManagerData.filteredFiles?.length) {
          const newFilteredFiles = structuredClone(fileManagerData?.filteredFiles).map((f: any) => {
            if (f.id == selectedFile.id) {
              f.name = newName;
              f.isFileNomenclatureCorrect = isNomenclatureCorrect;
            }
            return f;
          });
          newObj.filteredFiles = newFilteredFiles;
        }
        setFileManagerData((prev: any) => ({ ...prev, ...newObj }));
        dispatch(
          showToastNotification({
            content: untranslated(`${newName} ${t("renamed successfully")}`),
            type: TOAST_TYPE_SUCCESS,
          }),
        );
      }
      fileManagerRef.current?.hideLoading();
    }
  };

  const handleCloseFolder = () => {
    setOpenFolderCreationModal(false);
    setFolderName("");
  };

  const handleFolderName = (args: React.ChangeEvent<HTMLInputElement>) => {
    setFolderName(args.target.value);
  };

  const handleCustomFieldsData = (data: any) => {
    setContainerFormTemplate(data);
  };

  const handleEmbedUrl = (args: any) => {
    setFileTypeValue(args.target.value);
  };

  const handleContainerName = (args: React.ChangeEvent<HTMLInputElement>) => {
    setContainerName(args.target.value);
  };

  const handleCreateFolder = async () => {
    handleCloseFolder();
    const data: any = {
      hierarchyId: currentFolder.id,
      resourceType: "RESOURCE_TYPE_FOLDER",
      spaceId,
      name: folderName,
      dateModified: Date.now(),
    };
    const allParams: any = {
      action: "create",
      path: "/",
      data: [data],
      name: folderName,
    };
    beforeSending(allParams);
    if (!allParams.cancel) {
      fileManagerRef.current?.showLoading();
      // /createFolder takes `data` as a flat object (not the dispatcher's one-element array).
      const res = await axiosWrapper.post<CreateFolderResponse>(`${getBackendUrl()}/fileSystemServer/createFolder`, {
        ...allParams,
        data: allParams.data[0],
      });
      fileManagerRef.current?.hideLoading();
      if (res.data.status && res.data.isDuplicate) {
        dispatch(
          showToastNotification({
            content: t("Folder with same name already exists."),
            type: TOAST_TYPE_WARNING,
          }),
        );
        refreshCurrentFolder();
      } else if (res.data.status) {
        dispatch(
          showToastNotification({
            content: t("Folder created successfully"),
            type: TOAST_TYPE_SUCCESS,
          }),
        );
        refreshCurrentFolder();
      } else {
        dispatch(
          showToastNotification({
            content: untranslated(res.data.errorMessage ?? ""),
            type: TOAST_TYPE_ERROR,
          }),
        );
      }
    }
  };

  const handleNomenclatureChange = (e: any, args: any, field: any, index: number) => {
    const value = e.value ?? e.target.value;
    const nomenclatureArr = [...(nomenclatureRef.current ?? [])];

    nomenclatureArr[index] = value;
    nomenclatureRef.current = nomenclatureArr;
    setNomenclatureValues(nomenclatureArr);

    const separator = _.get(currentFolder, "fileNomenclature.separator", "");
    setContainerName(nomenclatureArr.join(separator));
  };

  const menuOpen = (selectedFiles: any[] = []) => {
    const searchParams = new URLSearchParams(location.search);
    const disableItems: string[] = [];
    if (searchParams.get("spaceType") !== TAB_EXTERNAL_SPACE) {
      selectedFiles.forEach((fileDetail: any) => {
        if (!fileDetail?.permission?.read) {
          disableItems.push("open");
        }
        if (!fileDetail?.permission?.delete) {
          disableItems.push("delete");
        }
        if (
          [
            "RESOURCE_TYPE_FOLDER",
            "RESOURCE_TYPE_SHORTCUT_FILE",
            "RESOURCE_TYPE_MERGED_FILE",
            "RESOURCE_TYPE_CONTRACT",
            "RESOURCE_TYPE_ESTIMATE",
            "RESOURCE_TYPE_SCHEDULE",
          ].includes(fileDetail.resourceType)
        ) {
          disableItems.push("CreateReview");
        }
        if (
          ![
            "RESOURCE_TYPE_MERGED_FILE",
            "RESOURCE_TYPE_FILE",
            "RESOURCE_TYPE_FOLDER",
            "RESOURCE_TYPE_SHORTCUT_FILE",
          ].includes(fileDetail.resourceType) ||
          !fileDetail?.permission?.download ||
          fileDetail.isEmptyContainer
        ) {
          disableItems.push("download");
        }
        if (!fileDetail?.permission?.share) {
          disableItems.push("transmit");
          disableItems.push("CreateReview");
        }
        if (!fileDetail?.permission?.write) {
          disableItems.push("rename");
          disableItems.push("cut");
          disableItems.push("newFolder");
        }
        if (!fileDetail?.permission?.upload) {
          disableItems.push("upload");
        }
        if (fileDetail?.hierarchyId == "0") {
          disableItems.push("cut");
        }
        if (
          !DRAWING_TYPE.includes(fileDetail?.name.split(".")[fileDetail?.name.split(".").length - 1]) &&
          fileDetail?.resourceType !== "RESOURCE_TYPE_MERGED_FILE"
        ) {
          disableItems.push("preloadAndOpen");
          disableItems.push("loadWithoutProperties");
        }
        if (fileDetail?.isFile) {
          disableItems.push("paste");
          if (fileManagerAsPopup) {
            disableItems.push("open");
            disableItems.push("preloadAndOpen");
            disableItems.push("loadWithoutProperties");
          }
        }
        if (!fileDetail?.isFile || fileDetail?.isContainer || !fileDetail?.permission?.download) {
          disableItems.push("copy");
        }
      });
      if (!filesToCopy && !filesToCut) {
        disableItems.push("paste");
      }
      if (selectedFiles.length > 1) {
        disableItems.push("open");
        disableItems.push("rename");
        disableItems.push("paste");
      }
    }
    if (fileManagerAsPopup) {
      disableItems.push("upload");
      disableItems.push("new");
    }
    const uniqueDisableItems = [...new Set(disableItems)];
    return uniqueDisableItems;
  };

  // File manager options mapper
  const fileManagerOptionsMapper: any = {
    [TAB_THIS_SPACE]: {
      layoutOptions: [
        {
          id: "refresh",
          name: "Refresh",
          icon: "refresh",
          onClick: refreshCurrentFolder,
        },
        {
          id: "paste",
          name: "Paste",
          icon: "Duplicate",
          onClick: handleResourcePaste,
        },
        {
          id: "selectAll",
          name: "Select All",
          icon: "selectAll",
          onClick: handleSelectAllOption,
        },
        {
          id: "unSelectAll",
          name: "Un-Select All",
          icon: "unSelectAll",
          onClick: handleUnSelectAllOption,
        },
      ],
      fileOptions: [
        {
          id: "open",
          name: "Open",
          icon: "folder",
          onClick: (selectedFiles: any[]) => handleOpenOrPreloadAndOpen(selectedFiles, "open"),
        },
        {
          id: "preloadAndOpen",
          name: "Preload & Open",
          icon: "folder",
          onClick: (selectedFiles: any[]) => handleOpenOrPreloadAndOpen(selectedFiles, "preloadAndOpen"),
        },
        {
          id: "loadWithoutProperties",
          name: "Load without properties",
          icon: "folder",
          onClick: (selectedFiles: any[]) => handleOpenOrPreloadAndOpen(selectedFiles, "loadWithoutProperties"),
        },
        {
          id: "delete",
          name: "Delete",
          icon: "delete",
          onClick: handleDeleteClick,
        },
        {
          id: "download",
          name: "Download",
          icon: "download",
          onClick: handleDownloadFile,
        },
        {
          id: "rename",
          name: "Rename",
          icon: "editProfile",
          onClick: handleRenameFile,
        },
        {
          id: "cut",
          name: "Cut",
          icon: "cut",
          onClick: handleResourceCut,
        },
        {
          id: "copy",
          name: "Copy",
          icon: "copy",
          onClick: handleResourceCopy,
        },
        {
          id: "transmit",
          name: "Transmit",
          icon: "Transmittals",
          onClick: handleFileTransmit,
        },
        {
          // Only shown for CAD drawings (gated by `extensions` in useFileManagerFiles).
          id: "exportToPdf",
          name: t("Export to PDF"),
          icon: "download",
          extensions: ["dwg", "dxf"],
          onClick: handleExportToPdf,
        },
        {
          id: "CreateReview",
          name: "Create a Review",
          icon: "Review",
          onClick: () => setCreateReviewModal({ show: true }),
        },
      ],
      folderOptions: [
        {
          id: "open",
          name: "Open",
          icon: "folder",
          onClick: (selectedFiles: any[]) => handleOpenOrPreloadAndOpen(selectedFiles, "open"),
        },
        {
          id: "delete",
          name: "Delete",
          icon: "delete",
          onClick: handleDeleteClick,
        },
        {
          id: "download",
          name: "Download",
          icon: "download",
          onClick: handleDownloadFile,
        },
        {
          id: "rename",
          name: "Rename",
          icon: "editProfile",
          onClick: handleRenameFile,
        },
        {
          id: "cut",
          name: "Cut",
          icon: "cut",
          onClick: handleResourceCut,
        },
        {
          id: "paste",
          name: "Paste",
          icon: "Duplicate",
          onClick: handleResourcePaste,
        },
      ],
    },
    [TAB_EXTERNAL_SPACE]: {
      layoutOptions: [
        {
          id: "refresh",
          name: "Refresh",
          icon: "refresh",
          onClick: refreshCurrentFolder,
        },
        {
          id: "selectAll",
          name: "Select All",
          icon: "selectAll",
          onClick: handleSelectAllOption,
        },
        {
          id: "unSelectAll",
          name: "Un-Select All",
          icon: "unSelectAll",
          onClick: handleUnSelectAllOption,
        },
      ],
      fileOptions: [
        {
          id: "open",
          name: "Open",
          icon: "folder",
          onClick: (selectedFiles: any[]) => handleOpenOrPreloadAndOpen(selectedFiles, "open"),
        },
        {
          id: "createNewShortcut",
          name: t("Create new shortcut"),
          icon: "shortcut",
          onClick: (selectedFiles: any[]) => {
            if (selectedFiles[0].folderType == 0) {
              setShortCutData({
                transmittalId: selectedFiles[0].transmittalVersionId,
                fileVersionIds: selectedFiles.map((x: any) => x.fileVersion),
              });
              setOpenFileModalExternal("shortcut");
            }
          },
        },
        {
          id: "consume",
          name: t("Consume"),
          icon: "consume",
          onClick: (selectedFiles: any[]) => {
            setOpenFileModalExternal("consume");
            setShortCutData(selectedFiles);
          },
        },
      ],
      folderOptions: [
        {
          id: "open",
          name: t("Open"),
          icon: "folder",
          onClick: (selectedFiles: any[]) => handleOpenOrPreloadAndOpen(selectedFiles, "open"),
        },
      ],
    },
    reviews: {
      layoutOptions: [],
      fileOptions: [],
      folderOptions: [],
    },
  };

  // Dropdown button items
  const dropDownButtonItems: any[] = [
    { text: t("Upload File"), id: "fileUpload", iconCss: "e-upload-file", iconName: "uploadFile" },
    { text: t("Upload Folder"), id: "folderUpload", iconCss: "e-upload-folder", iconName: "uploadFolder" },
    { seperator: true, id: "seperator1" },
    { text: t("New Folder"), id: "newFolder", iconCss: "e-new-folder", iconName: "newFolder" },
    { seperator: true, id: "seperator2" },
    { text: t("Merge"), id: "merge", iconCss: "e-new-merge", iconName: "merge" },
    { text: t("Container"), id: "container", iconCss: "e-new-container", iconName: "container" },
  ];

  if (showExcelImport) {
    dropDownButtonItems.push({
      text: t("Import"),
      id: "import",
      iconCss: "e-new-importExcel",
      iconName: "ImportExcel",
      hasSubmenu: true,
      subItems: [
        { text: t("Container"), id: "importContainer", iconName: "container" },
        { text: t("File"), id: "importFile", iconName: "files" },
      ],
    });
  }
  if (layout?.ATTACHED_EXTERNAL_VOLUME) {
    dropDownButtonItems.push({
      text: t("Import"),
      id: "fileImport",
      iconCss: "e-upload-file",
      iconName: "uploadFile",
    });
  }

  // Compose data layer (used directly by mobile; web's Syncfusion drives data so auto-load is disabled)
  const fileManagerComponent = useFileManagerComponent({
    fileManagerData,
    setFileManagerData,
    setCurrentFolder,
    beforeSending,
    onSuccess,
    showFolderTree,
    path: path ?? "/",
    workspaceId: spaceId,
    disableAutoLoad: !nativeMobile,
  });

  const fileManagerSharedState = useFileManagerShared({
    setSelectedFolder: fileManagerComponent.setSelectedFolder,
    fileManagerData,
    tempFolder: fileManagerComponent.tempFolder,
    handleSearchFiles: fileManagerComponent.handleSearchFiles,
    selectedFolder: fileManagerComponent.selectedFolder,
    preSearchText: preSearchText ?? "",
    onFileLoad,
  });

  // Effects
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearTimeout(pollingRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setPreSearchText(fileSearchText);
  }, [fileSearchText]);

  useEffect(() => {
    if (spaceType) {
      setSelectedTab(spaceType);
    }
  }, [spaceType]);

  const searchParamsString = searchParams.toString();
  useEffect(() => {
    tabRef.current.isExternalSpace = searchParams.get("spaceType") === TAB_EXTERNAL_SPACE;
    tabRef.current.isThisSpace = searchParams.get("spaceType") === TAB_THIS_SPACE;
  }, [searchParamsString]);

  useEffect(() => {
    if (!defaultPath) {
      setPath("/");
    } else {
      setPath(defaultPath);
    }
  }, [defaultPath]);

  useEffect(() => {
    if (nativeMobile || typeof document === "undefined") return;
    document.title = `Files | ${projectData?.description}`;
  }, [projectData, nativeMobile]);

  useEffect(() => {
    if (refreshFileManager) {
      dispatch(refreshFileManagerFn(false));
      refreshCurrentFolder();
    }
    return () => {
      dispatch(refreshFileManagerFn(false));
    };
  }, [refreshFileManager]);

  useEffect(() => {
    getFormTemplate();
    dispatch(setCurrentFolderInFileManager(currentFolder));
    if (nativeMobile) return;
    if (!fileManagerAsPopup && currentFolder?.id != "0") {
      const innerPath = currentFolder.parentIdArray?.filter((id: any) => id != "0").join("/");
      const initialPath = innerPath ? `/${innerPath}` : "";
      let folderPath = `${initialPath}/${currentFolder.id}/`;
      const encodedFolderPath = encodeURIComponent(folderPath);
      navigate(`${location.pathname}${location.search}#folderPath=${encodedFolderPath}`, { replace: true });
    } else if (!fileManagerAsPopup && currentFolder?.id == "0") {
      navigate(`${location.pathname}${location.search}`, { replace: true });
    }
  }, [currentFolder]);

  useEffect(() => {
    if (fileUploadState == "CLOSED") {
      dispatch(setIsFileVersionUploading(false));
      dispatch(closeFileUpload());
      dispatch(emptyFileUploadData());
    }
  }, [fileUploadState]);

  useEffect(() => {
    fileUploadListRef.current = [...(fileUploadList ?? [])];
  }, [fileUploadList]);

  useEffect(() => {
    if (fileUploadState === "CLOSED") return;
    if ((fileUploadList ?? []).length) {
      dispatch(minimiseFileUpload());
    } else {
      dispatch(closeFileUpload());
    }
  }, [location]);

  useEffect(() => {
    setFileAllowedExtensions({
      minFileSize: 0,
      maxFileSize: MAX_FILE_UPLOAD_SIZE,
      allowedExtensions: "",
    });
  }, []);

  useEffect(() => {
    if (openThisSpaceByDefault || createShortcut) {
      setSelectedTab(TAB_THIS_SPACE);
    }
    return () => {
      if (typeof handleFileSelectionCallback === "function") {
        handleFileSelectionCallback([]);
      }
      if (!fileManagerAsPopup) {
        dispatch(emptyCurrentFolderInFileManager());
      }
      dispatch(setSpaceData({}));
    };
  }, []);

  useEffect(() => {
    reloadFileManager();
    setToolBarBtnState((prev) => ({
      ...prev,
      upload: selectedTab == TAB_THIS_SPACE && !hideUpload,
      refresh: selectedTab == TAB_THIS_SPACE || selectedTab == TAB_EXTERNAL_SPACE,
      rename: prev.rename && selectedTab == TAB_THIS_SPACE,
      delete: prev.delete && selectedTab == TAB_THIS_SPACE,
      download: prev.download && selectedTab == TAB_THIS_SPACE,
      details: selectedTab == TAB_THIS_SPACE || selectedTab == TAB_EXTERNAL_SPACE,
      consume: prev.consume && selectedTab == TAB_EXTERNAL_SPACE,
      createNewShortcut: prev.createNewShortcut && selectedTab == TAB_EXTERNAL_SPACE,
    }));
  }, [selectedTab, hideUpload]);

  useEffect(() => {
    if (Array.isArray(alreadySelected) && fileManagerRef.current) {
      const currentFiles = fileManagerRef.current?.dataView?.items || []; // ? because dataView is null in case of large icons mode
      const selectTheseRows: number[] = [];
      alreadySelected.forEach((file: any) => {
        const indexOfItem = currentFiles.findIndex((x: any) => x.hierarchyId == file.hierarchyId);
        if (!isNaN(indexOfItem) && indexOfItem != -1) {
          selectTheseRows.push(indexOfItem);
        }
      });
      fileManagerRef.current?.setSelectedRows?.(selectTheseRows);
    }
  }, [fileManagerRef.current, currentFolder]);

  useEffect(() => {
    if (spaceId) {
      dispatch(setSpaceToggle({ spaceId }));
      reloadFileManager();
    }
  }, [spaceId]);

  return {
    // Refs
    fileManagerRef,
    formTemplateRef,
    formValidator,
    fileUploadListRef,
    nomenclatureRef,
    spaceListRef,
    tabRef,
    pollingRef,

    // State
    fileManagerData,
    setFileManagerData,
    showContainerImportModal,
    setShowContainerImportModal,
    importMode,
    setImportMode,
    openRenameModal,
    setOpenRenameModal,
    selectedFile,
    setSelectedFile,
    newName,
    setNewName,
    folderName,
    setFolderName,
    createReviewModal,
    setCreateReviewModal,
    fileAllowedExtensions,
    setFileAllowedExtensions,
    openMergeModal,
    setOpenMergeModal,
    openMergeEstimateModal,
    setOpenMergeEstimateModal,
    emptyThisSpace,
    setEmptyThisSpace,
    emptyExternal,
    setEmptyExternal,
    openFolderCreationModal,
    setOpenFolderCreationModal,
    openFileImport,
    setOpenFileImport,
    openContainerModal,
    setOpenContainerModal,
    currentFolder,
    setCurrentFolder,
    selectedFilesToDelete,
    setSelectedFilesToDelete,
    openDeleteConfirmationModal,
    setOpenDeleteConfirmationModal,
    openCreateTransmittalModal,
    setOpenCreateTransmittalModal,
    filesToCopy,
    setFilesToCopy,
    filesToCut,
    setFilesToCut,
    formTemplateReceived,
    setFormTemplateReceived,
    containerFormTemplate,
    setContainerFormTemplate,
    fileTypeValue,
    setFileTypeValue,
    resourceType,
    setResourceType,
    containerName,
    setContainerName,
    draggedFiles,
    setDraggedFiles,
    path,
    setPath,
    preSearchText,
    setPreSearchText,
    toolBarBtnState,
    setToolBarBtnState,
    selectedTab,
    setSelectedTab,
    shortCutData,
    setShortCutData,
    openFileModalExternal,
    setOpenFileModalExternal,
    anchorEl,
    setAnchorEl,
    open,
    importSubmenuAnchor,
    importSubmenuOpen,

    // Redux state
    layout,
    getSpaceToggle,
    projectData,
    userDetails,
    fileUploadList,
    isFileVersionUploading,
    fileUploadState,
    spaceData,
    refreshFileManager,

    // Handlers
    showToast,
    handleDeleteClick,
    handleDeleteModalClose,
    handleDeleteSuccess,
    getSelectedFilesInFileManager,
    handleRenameFile,
    handleFileTransmit,
    fetchConstituentFilesOfMergedFile,
    handleLocationOpen,
    handleOpenOrPreloadAndOpen,
    handleDownloadFile,
    getPermission,
    beforeSending,
    handleEmptyStateDrop,
    handleEmptyStateDragOver,
    handleResourceCut,
    handleResourceCopy,
    handleResourcePaste,
    handleSelectAllOption,
    handleUnSelectAllOption,
    reloadFileManager,
    refreshCurrentFolder,
    handleFileManagerTabClick,
    handleSpaceSelect,
    handleSpaceToggle,
    handleDetailIconClick,
    getFormTemplate,
    handleFileAndFolderUpload,
    handleSelectOption,
    handleClick,
    handleClose,
    handleImportSubmenuOpen,
    handleImportSubmenuClose,
    closeReviewModal,
    onFileSelect,
    onFileClick,
    toolbarClick,
    onSuccess,
    saveFileMergeModal,
    closeFileMergeModal,
    closeCreateShortcutModal,
    closeFileImportPopup,
    closeMergeEstimateModal,
    handleFileImportCallback,
    createMergeEstimateModal,
    onFileLoad,
    handleNoDataRefresh,
    handleModalClose,
    handleCreateContainer,
    handleSaveNewName,
    handleCloseFolder,
    handleFolderName,
    handleCustomFieldsData,
    handleEmbedUrl,
    handleContainerName,
    handleCreateFolder,
    handleNomenclatureChange,
    nomenclatureValues,
    menuOpen,

    // Config
    fileManagerOptionsMapper,
    dropDownButtonItems,

    // Props passed through
    fileManagerAsPopup,
    hideAllTabs,
    hideUpload,
    hideExternalSpace,
    hideReviews,
    enableSpaceListDropdown,
    disableContextMenu,
    showFolderTree,
    disableDragAndDropUpload,
    showExcelImport,
    mobileFileManager,
    enableOnlyShareableResources,
    disableFiles,
    enableFilesTypes,
    createShortcut,
    allowFolderSelectionCallback,
    spaceList,
    spaceId,
    projectId,

    // Composed data layer (used by mobile)
    fileManagerComponent,
    fileManagerSharedState,

    // Top-level re-exports from the composed data layer (so mobile can use only useFileManager)
    selectedFolder: fileManagerComponent.selectedFolder,
    setSelectedFolder: fileManagerComponent.setSelectedFolder,
    tempFolder: fileManagerComponent.tempFolder,
    refreshGrid: fileManagerComponent.refreshGrid,
    isLoaded: fileManagerComponent.isLoaded,
    refreshFiles: fileManagerComponent.refreshFiles,
    deleteFiles: fileManagerComponent.deleteFiles,
    isFolder: fileManagerComponent.isFolder,
    canViewFile: fileManagerComponent.canViewFile,
    canDeleteFile: fileManagerComponent.canDeleteFile,
    breadCrumbData: fileManagerSharedState.breadCrumbData,
    filesData: fileManagerSharedState.filesData,
    query: fileManagerSharedState.query,
    setQuery: fileManagerSharedState.setQuery,
    showPathColumn: fileManagerSharedState.showPathColumn,

    // Translation
    t,
  };
}

export default useFileManager;
